import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, List, ListItem, ListItemText, Paper, Skeleton, Stack, TextField, Typography } from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Layout from '../../components/Layout';
import { api } from '../../services/api/apiHandler';
import { uploadFile } from '../../utils/helper';
import { emailTemplateApi, EmailEventType, TemplateContent, TemplateContext, TemplateOption, SavedTemplate, EmailPreview } from '../../services/emailTemplates';

const emailTypes = { AHC: 'AHC — Annual health check-up', PEHC: 'PEHC — Pre-employment check-up', CAMP: 'CAMP — Health camp', onboarding: 'User onboarding' };
const fieldLabels: Record<string, string> = {
  employee_name: 'Employee name', employee_email: 'Employee email', employee_phone: 'Employee mobile', company_name: 'Corporate name',
  event_name: 'Event name', event_type: 'Event type', event_start_date: 'Event start date', event_end_date: 'Event end date',
  manual_note: 'User manual note', android_url: 'Android app link', ios_url: 'iOS app link', support_email: 'Support email',
  support_phone: 'Support phone', logo_url: 'Logo URL',
};
const commonFields = ['employee_name', 'company_name', 'event_name', 'event_type', 'employee_email'];

export default function EmailTemplates() {
  const [emailType, setEmailType] = useState<EmailEventType | 'onboarding'>('AHC');
  const [corporateId, setCorporateId] = useState('');
  const [corporates, setCorporates] = useState<TemplateOption[]>([]);
  const [template, setTemplate] = useState<SavedTemplate | null>(null);
  const [content, setContent] = useState<TemplateContent>({ subject: '', html: '' });
  const [source, setSource] = useState('built-in');
  const [placeholders, setPlaceholders] = useState<string[]>([]);
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [mode, setMode] = useState<'visual' | 'html' | 'layout'>('visual');
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [optionsError, setOptionsError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [notice, setNotice] = useState('');
  const [revision, setRevision] = useState(0);
  const [optionsRevision, setOptionsRevision] = useState(0);
  const epoch = useRef(0);
  const previewEpoch = useRef(0);
  const busyRef = useRef(false);
  const quill = useRef<ReactQuill>(null);
  const subjectInput = useRef<HTMLInputElement>(null);
  const htmlInput = useRef<HTMLTextAreaElement>(null);
  const imageInput = useRef<HTMLInputElement>(null);
  const attachmentInput = useRef<HTMLInputElement>(null);
  const previewPane = useRef<HTMLDivElement>(null);
  const insertion = useRef(0);
  const lastField = useRef<'subject' | 'html'>('html');
  const currentMode = useRef(mode); currentMode.current = mode;
  const currentHtml = useRef(content.html); currentHtml.current = content.html;
  const context = useMemo<TemplateContext>(() => ({
    kind: emailType === 'onboarding' ? 'onboarding' : 'event_registration', scope: corporateId ? 'corporate' : 'default',
    ...(corporateId ? { corporateId } : {}), ...(emailType !== 'onboarding' ? { eventType: emailType } : {}),
  }), [emailType, corporateId]);

  useEffect(() => {
    let current = true;
    setOptionsError('');
    emailTemplateApi.corporates().then(data => { if (current) setCorporates(data); })
      .catch(caught => { if (current) setOptionsError(caught.message); });
    return () => { current = false; };
  }, [optionsRevision]);
  useEffect(() => {
    const request = ++epoch.current;
    setLoaded(false); setLoading(true); setContent({ subject: '', html: '' }); setTemplate(null); setPreview(null);
    setError(''); setLoadError(''); setConflict(false); setNotice(''); setDirty(false); previewEpoch.current++;
    emailTemplateApi.get(context).then(result => {
      if (request !== epoch.current) return;
      const draft = result.template || result.effective;
      setTemplate(result.template); setContent({ subject: draft.subject, html: draft.html, ...(draft.attachments ? { attachments: draft.attachments } : {}) }); setSource(result.inheritedFrom);
      setPlaceholders(result.placeholders); setLoaded(true);
      const fullLayout = /^\s*<table(?:\s|>)/i.test(draft.html);
      setMode(fullLayout ? 'layout' : 'visual');
      if (fullLayout) {
        lastField.current = 'subject';
        const previewRequest = ++previewEpoch.current;
        void emailTemplateApi.preview(context, draft).then(value => {
          if (request === epoch.current && previewRequest === previewEpoch.current) setPreview(value);
        }).catch(caught => { if (request === epoch.current && previewRequest === previewEpoch.current) setError(caught.message); });
      }
    }).catch(caught => { if (request === epoch.current) setLoadError(caught.message); })
      .finally(() => { if (request === epoch.current) setLoading(false); });
    return () => { epoch.current++; };
  }, [context, revision]);

  const chooseImage = useCallback(() => {
    if (busyRef.current || currentMode.current === 'layout') return;
    insertion.current = currentMode.current === 'html' ? htmlInput.current?.selectionStart ?? currentHtml.current.length
      : quill.current?.getEditor().getSelection(true)?.index ?? Math.max(0, (quill.current?.getEditor().getLength() || 1) - 1);
    imageInput.current?.click();
  }, []);
  const modules = useMemo(() => ({ toolbar: { container: [
    [{ header: [1, 2, 3, false] }], ['bold', 'italic', 'underline'], [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }], [{ align: [] }], ['link', 'image'], ['clean'],
  ], handlers: { image: chooseImage } } }), [chooseImage]);
  useEffect(() => {
    if (mode !== 'visual' || !loaded) return;
    const editor = quill.current?.getEditor();
    if (!editor) return;
    editor.root.setAttribute('role', 'textbox'); editor.root.setAttribute('aria-label', 'Email body'); editor.root.setAttribute('aria-multiline', 'true');
    const labels: Record<string, string> = { header: 'Heading style', bold: 'Bold', italic: 'Italic', underline: 'Underline',
      color: 'Text color', background: 'Highlight color', list: 'List style', align: 'Alignment', link: 'Insert link', image: 'Insert image', clean: 'Clear formatting' };
    const toolbar: HTMLElement = editor.getModule('toolbar').container;
    toolbar.querySelectorAll<HTMLElement>('button, .ql-picker-label, .ql-picker-item').forEach(control => {
      const classes = control.closest('.ql-picker')?.className || control.className;
      const format = Object.keys(labels).find(key => classes.split(' ').includes(`ql-${key}`));
      if (format) {
        const value = control.getAttribute('value') || control.getAttribute('data-value');
        control.setAttribute('aria-label', `${labels[format]}${value ? ` ${value}` : ''}`);
      }
    });
  }, [loaded, mode]);

  const update = (field: 'subject' | 'html', value: string) => {
    previewEpoch.current++; setContent(previous => ({ ...previous, [field]: value })); setDirty(true); setPreview(null); setNotice('');
  };
  const insertField = (name: string) => {
    if (busyRef.current) return;
    const token = `{{${name}}}`;
    if (lastField.current === 'subject' || mode === 'html') {
      const field = lastField.current === 'subject' ? 'subject' : 'html';
      const input = field === 'subject' ? subjectInput.current : htmlInput.current;
      const start = input?.selectionStart ?? content[field].length;
      const end = input?.selectionEnd ?? start;
      update(field, `${content[field].slice(0, start)}${token}${content[field].slice(end)}`);
      requestAnimationFrame(() => { input?.focus(); input?.setSelectionRange(start + token.length, start + token.length); });
    } else {
      const editor = quill.current?.getEditor();
      if (!editor) return;
      const range = editor.getSelection(true);
      const index = range?.index ?? Math.max(0, editor.getLength() - 1);
      if (range?.length) editor.deleteText(index, range.length, 'api');
      editor.insertText(index, token, 'api'); editor.setSelection(index + token.length, 0, 'silent');
      update('html', editor.root.innerHTML);
    }
  };
  const insertImage = async (file?: File) => {
    if (!file || busyRef.current || currentMode.current === 'layout') return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024) {
      setError('Choose a JPG, PNG or WebP image up to 5 MB.'); return;
    }
    const request = epoch.current;
    previewEpoch.current++; busyRef.current = true; setBusy(true); setError(''); setPreview(null);
    try {
      const result = await uploadFile({ module: 'email-templates' }, [file]);
      const path = result.data?.data?.files?.[0]?.url;
      if (result.error || typeof path !== 'string') throw new Error(result.message || 'Image upload failed. Try Insert image again.');
      const url = new URL(path, new URL(api.defaults.baseURL || '/', window.location.origin)).href;
      if (!/^https?:/.test(url)) throw new Error('Unsupported image URL.');
      if (request !== epoch.current) return;
      if (currentMode.current === 'html') {
        const index = insertion.current;
        update('html', `${currentHtml.current.slice(0, index)}<img src="${url.replace(/"/g, '&quot;')}" alt="" style="max-width:100%;height:auto">${currentHtml.current.slice(index)}`);
      } else {
        const editor = quill.current?.getEditor();
        if (!editor) return;
        editor.insertEmbed(insertion.current, 'image', url, 'api'); editor.setSelection(insertion.current + 1, 0, 'silent');
        update('html', editor.root.innerHTML);
      }
    } catch (caught: any) { if (request === epoch.current) setError(caught.message); }
    finally { if (request === epoch.current) { busyRef.current = false; setBusy(false); } }
  };
  const addAttachments = async (files: File[]) => {
    if (!files.length || busyRef.current || !loaded) return;
    const existing = content.attachments || [];
    const allowed = /\.(pdf|jpe?g|png|webp|gif|docx?|xlsx?|pptx?|txt|csv)$/i;
    if (files.some(file => !allowed.test(file.name))) { setError('Choose PDF, images, Word, Excel, PowerPoint, TXT or CSV files.'); return; }
    if (existing.length + files.length > 5 || files.reduce((total, file) => total + file.size, existing.reduce((total, file) => total + (file.size || 0), 0)) > 10 * 1024 * 1024) {
      setError('Choose up to 5 attachments totalling no more than 10 MB.'); return;
    }
    const request = epoch.current;
    busyRef.current = true; setBusy(true); setError(''); setNotice('');
    try {
      const uploaded = await emailTemplateApi.uploadAttachments(files);
      if (request !== epoch.current) return;
      previewEpoch.current++; setPreview(null);
      setContent(previous => ({ ...previous, attachments: [...previous.attachments || [], ...uploaded] })); setDirty(true);
    } catch (caught: any) { if (request === epoch.current) setError(caught.message); }
    finally { if (request === epoch.current) { busyRef.current = false; setBusy(false); } }
  };

  const act = async (action: 'preview' | 'save' | 'reset' | 'demo') => {
    if (busyRef.current || !loaded || loading) return;
    if (action === 'reset' && (!template || !window.confirm('Remove this custom email and use the default email for this selection?'))) return;
    const request = epoch.current;
    previewEpoch.current++; busyRef.current = true; setBusy(true); setError(''); setConflict(false); setNotice('');
    try {
      if (action !== 'reset') {
        const checked = await emailTemplateApi.preview(context, content);
        if (request !== epoch.current) return;
        setPreview(checked);
        if (action === 'preview' && window.innerWidth < 1200) requestAnimationFrame(() => previewPane.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }));
        if (action === 'save') {
          const result = await emailTemplateApi.save(context, content, true, template?.__v ?? null, template?._id);
          if (request !== epoch.current) return;
          setTemplate(result.template); setContent({ subject: result.template.subject, html: result.template.html, ...(result.template.attachments ? { attachments: result.template.attachments } : {}) }); setDirty(false);
          setNotice(result.changedBySanitizer ? 'Email saved. Check the preview for the final formatting.' : 'Email saved. New registrations will use this email.');
        }
        if (action === 'demo') {
          const result = await emailTemplateApi.sendDemo(context, content);
          if (request !== epoch.current) return;
          setNotice(`Demo email sent to ${result.recipients.join(', ')}.`);
        }
      } else {
        await emailTemplateApi.remove(template!._id);
        if (request === epoch.current) setRevision(value => value + 1);
      }
    } catch (caught: any) { if (request === epoch.current) { setError(caught.message); setConflict(caught.status === 409); } }
    finally { if (request === epoch.current) { busyRef.current = false; setBusy(false); } }
  };
  const canSwitch = () => !dirty || window.confirm('Discard your unsaved email changes and switch?');
  const fields = (names: string[]) => names.filter(name => placeholders.includes(name)).map(name =>
    <Chip key={name} clickable disabled={busy} label={fieldLabels[name] || name} title={`{{${name}}}`}
      aria-label={`Insert {{${name}}}`} onMouseDown={event => event.preventDefault()} onClick={() => insertField(name)} />);

  return <Layout appBarTitle="Email Templates"><Box sx={{ p: { xs: 1, md: 2 } }}>
    <Typography variant="h4" gutterBottom>Email templates</Typography>
    <Typography color="text.secondary" sx={{ mb: 3 }}>Choose the email type and corporate, write your message, then save.</Typography>
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField select fullWidth label="Email type" value={emailType} SelectProps={{ native: true }} disabled={busy}
          onChange={event => { if (canSwitch()) setEmailType(event.target.value as EmailEventType | 'onboarding'); }}>
          {Object.entries(emailTypes).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </TextField>
        <TextField select fullWidth label="Corporate" value={corporateId} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} disabled={busy}
          onChange={event => { if (canSwitch()) setCorporateId(event.target.value); }}>
          <option value="">All corporates (default)</option>{corporates.map(item => <option key={item._id} value={item._id}>{item.companyName || item.name}</option>)}
        </TextField>
      </Stack>
      {loaded && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }} alignItems={{ sm: 'center' }}>
        <Chip size="small" label={template?.isActive ? 'Custom email saved' : template ? 'Not active — save to enable' : source === 'built-in' ? 'Standard email' : source === 'corporate' ? 'Corporate email' : 'Default email'} />
        <Typography variant="body2" color="text.secondary">Applies to new {emailType === 'onboarding' ? 'users' : `${emailType} registrations`}. Existing registrations are not emailed again.</Typography>
      </Stack>}
    </Paper>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}{conflict && <Button disabled={busy} onClick={() => {
      if (window.confirm('Reload the saved email and discard your unsaved draft? Copy changes you want to keep first.')) setRevision(value => value + 1);
    }}>Reload saved email</Button>}</Alert>}
    {loadError && <Alert severity="error" sx={{ mb: 2 }} action={<Button disabled={busy} onClick={() => setRevision(value => value + 1)}>Retry loading</Button>}>{loadError}</Alert>}
    {optionsError && <Alert severity="error" sx={{ mb: 2 }} action={<Button disabled={busy} onClick={() => setOptionsRevision(value => value + 1)}>Retry corporates</Button>}>{optionsError}</Alert>}
    {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
    {loading ? <Stack spacing={2}><Skeleton height={60} /><Skeleton variant="rectangular" height={360} /></Stack> : loaded &&
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1fr) minmax(0,1fr)' }, gap: 3 }}>
        <Box sx={{ minWidth: 0 }}>
          <TextField fullWidth label="Subject" value={content.subject} inputRef={subjectInput} inputProps={{ maxLength: 180 }} disabled={busy}
            onFocus={() => { lastField.current = 'subject'; }} onChange={event => update('subject', event.target.value)} />
          <Box sx={{ my: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Insert a field</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Click a field to insert it at the cursor in your subject or message. Details fill in automatically.</Typography>
            <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>{fields(commonFields)}</Stack>
            <Box component="details" sx={{ mt: 1 }}><Typography component="summary" variant="body2">More fields</Typography>
              <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} sx={{ mt: 1 }}>{fields(placeholders.filter(name => !commonFields.includes(name)))}</Stack>
            </Box>
          </Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">Email message</Typography>
            <Stack direction="row"><Button disabled={busy || mode === 'layout'} onClick={chooseImage}>Insert image</Button>
              {mode !== 'layout' && <Button disabled={busy} title="Advanced HTML editing" onClick={() => {
                if (mode === 'html' && /^\s*<table(?:\s|>)/i.test(content.html) && !window.confirm('Visual editing simplifies table layouts and can change this design. Continue?')) return;
                setMode(mode === 'html' ? 'visual' : 'html'); lastField.current = 'html';
              }}>{mode === 'html' ? 'Visual editor' : 'HTML'}</Button>}</Stack>
          </Stack>
          <input ref={imageInput} type="file" accept="image/jpeg,image/png,image/webp" hidden aria-label="Upload template image"
            onChange={event => { const file = event.target.files?.[0]; event.target.value = ''; void insertImage(file); }} />
          <Box onFocus={() => { lastField.current = 'html'; }}>
            {mode === 'layout' ? <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Your email design is ready</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>The preview shows the final layout. Employee and corporate details fill in automatically when sent.</Typography>
              <Button disabled={busy} onClick={() => { setMode('html'); lastField.current = 'html'; }}>Edit HTML</Button>
            </Paper> : mode === 'visual' ? <Box sx={{ bgcolor: 'background.paper', '& .ql-editor': { minHeight: 300, fontSize: 15, lineHeight: 1.6 }, '& .ql-editor p': { mb: 1.5 }, '& .ql-editor img': { maxWidth: '100%' } }}>
              <ReactQuill ref={quill} value={content.html} modules={modules} readOnly={busy} onChange={(value, _delta, source) => { if (source === 'user') update('html', value); }} />
            </Box> : <TextField fullWidth multiline minRows={14} inputRef={htmlInput} label="Email HTML" value={content.html} disabled={busy}
              helperText="Advanced layouts can use HTML. Visual editing supports text, links and images."
              inputProps={{ maxLength: 100000, style: { fontFamily: 'monospace' } }} onChange={event => update('html', event.target.value)} />}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{mode === 'layout' ? 'Use this design as is, or choose Edit HTML to change its layout and images.' : 'Place the cursor anywhere in the message, then insert an image.'}</Typography>
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1">Attachments (optional)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Up to 5 files, 10 MB total. PDF, images, Word, Excel, PowerPoint, TXT or CSV. These files accompany the email and its demo.</Typography>
            <Button variant="outlined" disabled={busy || (content.attachments?.length || 0) >= 5} onClick={() => attachmentInput.current?.click()}>Add attachments</Button>
            <input ref={attachmentInput} type="file" multiple hidden aria-label="Upload email attachments" accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
              onChange={event => { const files = Array.from(event.target.files || []); event.target.value = ''; void addAttachments(files); }} />
            {!!content.attachments?.length && <List dense aria-label="Email attachments">
              {content.attachments.map((file, index) => <ListItem key={file.url} disableGutters secondaryAction={<Button size="small" disabled={busy} aria-label={`Remove attachment ${file.filename}`} onClick={() => {
                if (busyRef.current) return;
                previewEpoch.current++; setPreview(null); setNotice(''); setDirty(true);
                setContent(previous => ({ ...previous, attachments: previous.attachments?.filter((_file, item) => item !== index) }));
              }}>Remove</Button>}>
                <ListItemText primary={file.filename} secondary={file.size ? `${(file.size / 1024).toFixed(1)} KB` : undefined} sx={{ pr: 10, overflowWrap: 'anywhere' }} />
              </ListItem>)}
            </List>}
          </Box>
          <Stack direction="row" useFlexGap spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
            <Button variant="contained" disabled={busy || !content.subject.trim() || !content.html.trim()} onClick={() => void act('save')}>Save email</Button>
            <Button variant="outlined" disabled={busy || !content.subject.trim()} onClick={() => void act('preview')}>Preview email</Button>
            <Button variant="outlined" aria-describedby="demo-email-recipients" disabled={busy || !content.subject.trim() || !content.html.trim()} onClick={() => void act('demo')}>Send demo email</Button>
            {template && <Button color="error" disabled={busy} onClick={() => void act('reset')}>Restore default</Button>}
          </Stack>
          <Typography id="demo-email-recipients" variant="body2" color="text.secondary" sx={{ mt: 1 }}>Demo emails use sample details and go to it@myewacare.com, mala@myewacare.com, nethra@myewacare.com and support@myewacare.com. Sending a demo does not save your draft.</Typography>
          {busy && <Typography role="status" sx={{ mt: 1 }}>Processing your email...</Typography>}
        </Box>
        <Box ref={previewPane} sx={{ minWidth: 0 }}><Typography variant="h6" gutterBottom>Email preview</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Sample employee details. Preview and Save do not send an email.</Typography>
          {preview ? <><Typography sx={{ mb: 1 }}><strong>Subject:</strong> {preview.subject}</Typography>
            {mode === 'html' && preview.changedBySanitizer && <Alert severity="info" sx={{ mb: 1 }}>Email-safe formatting applied.</Alert>}
            <Box component="iframe" title="Email template preview" sandbox="" referrerPolicy="no-referrer" srcDoc={preview.htmlBody}
              sx={{ width: '100%', height: 520, border: '1px solid', borderColor: 'divider', bgcolor: 'white' }} />
          </> : <Paper variant="outlined" sx={{ p: 3 }}><Typography color="text.secondary">Choose Preview email to see the final message. Save also checks and previews it for you.</Typography></Paper>}
        </Box>
      </Box>}
  </Box></Layout>;
}
